import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  askAssistant,
  AssistantApiError,
  deleteAllHunterChatHistory,
  deleteHunterChatHistory,
  getHunterChatHistory,
  saveHunterChatMessage,
} from '../../services/assistant.service';
import {
  AskAssistantResult,
  AssistantCandidate,
} from '../../types/assistant.types';
import {
  ASSISTANT_HISTORY_PAGE_SIZE,
  clearMiniChatScroll,
  collectAssistantCandidateIdsNeedingHydration,
} from '../../utils/assistant.utils';
import candidateService from '../Candidate/Candidate.service';
import type { AppDispatch, RootState } from '../store';
import {
  AssistantAskPayload,
  AssistantAskRejectValue,
  LoadAssistantHistoryArg,
  LoadAssistantHistoryResult,
} from './Assistant.type';

let askAbortController: AbortController | null = null;

const NAME_HYDRATE_CONCURRENCY = 5;

export const abortAssistantAsk = () => {
  askAbortController?.abort();
  askAbortController = null;
};

const resolvePersonFullName = (person: {
  fullName?: string;
  firstName?: string;
  lastName?: string;
}): string => {
  if (person.fullName?.trim()) {
    return person.fullName.trim();
  }

  return [person.firstName, person.lastName]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
};

const resolvePersonEnglishLevel = (person: {
  english?: { level?: string } | null;
}): string => person.english?.level?.trim() || '';

const mapWithConcurrency = async <T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> => {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (nextIndex < items.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;

        try {
          const value = await mapper(items[currentIndex]);
          results[currentIndex] = { status: 'fulfilled', value };
        } catch (reason) {
          results[currentIndex] = { status: 'rejected', reason };
        }
      }
    },
  );

  await Promise.all(workers);
  return results;
};

export type HydrateAssistantCandidateMetaResult = {
  names: Record<string, string | null>;
  english: Record<string, string | null>;
};

export const hydrateAssistantCandidateNamesThunk = createAsyncThunk<
  HydrateAssistantCandidateMetaResult,
  string[],
  { state: RootState }
>(
  'assistant/hydrateAssistantCandidateNamesThunk',
  async (ids, { getState }) => {
    const { nameById, englishById } = getState().assistant;
    const uniqueIds = Array.from(
      new Set(ids.map(id => String(id).trim()).filter(Boolean)),
    );

    if (!uniqueIds.length) {
      return { names: {}, english: {} };
    }

    const settled = await mapWithConcurrency(
      uniqueIds,
      NAME_HYDRATE_CONCURRENCY,
      async id => {
        const response = await candidateService.getPreviewInfoIsolated(id);
        const person = response.data?.data ?? response.data;
        const fullName = resolvePersonFullName(person || {});
        const englishLevel = resolvePersonEnglishLevel(person || {});

        return { id, fullName, englishLevel };
      },
    );

    const names: Record<string, string | null> = {};
    const english: Record<string, string | null> = {};

    uniqueIds.forEach(id => {
      if (!(id in nameById)) {
        names[id] = null;
      }

      if (!(id in englishById)) {
        english[id] = null;
      }
    });

    for (const result of settled) {
      if (result.status !== 'fulfilled') {
        continue;
      }

      const { id, fullName, englishLevel } = result.value;

      if (!(id in nameById)) {
        names[id] = fullName || null;
      }

      if (!(id in englishById)) {
        english[id] = englishLevel || null;
      }
    }

    return { names, english };
  },
  {
    condition: (ids, { getState }) => {
      const { nameById, englishById, namesLoadingIds } = getState().assistant;
      const loadingSet = new Set(namesLoadingIds);

      return ids.some(id => {
        const normalized = String(id).trim();
        if (!normalized || loadingSet.has(normalized)) {
          return false;
        }

        const needsName = !(normalized in nameById);
        const needsEnglish = !(normalized in englishById);

        return needsName || needsEnglish;
      });
    },
  },
);

const dispatchNameHydration = (
  dispatch: AppDispatch,
  candidates: AssistantCandidate[] = [],
  getState: () => RootState,
) => {
  const { nameById, englishById } = getState().assistant;
  const ids = collectAssistantCandidateIdsNeedingHydration(
    candidates,
    nameById,
    englishById,
  );

  if (!ids.length) {
    return;
  }

  dispatch(hydrateAssistantCandidateNamesThunk(ids));
};

export const loadAssistantHistoryThunk = createAsyncThunk<
  LoadAssistantHistoryResult,
  LoadAssistantHistoryArg | undefined,
  { state: RootState; dispatch: AppDispatch }
>(
  'assistant/loadAssistantHistoryThunk',
  async (arg, { getState, dispatch }) => {
    const loadMore = Boolean(arg?.loadMore);
    const page = loadMore ? getState().assistant.historyNextPage : 1;
    const data = await getHunterChatHistory({
      page,
      perPage: ASSISTANT_HISTORY_PAGE_SIZE,
    });

    const candidates = data.items.flatMap(item => item.candidates ?? []);
    dispatchNameHydration(dispatch, candidates, getState);

    return {
      ...data,
      loadMore,
    };
  },
  {
    condition: (arg, { getState }) => {
      const { historyHydrated, historyHasMore, historyLoadingMore } =
        getState().assistant;

      if (arg?.loadMore) {
        return historyHydrated && historyHasMore && !historyLoadingMore;
      }

      return !historyHydrated;
    },
  },
);

export const askAssistantThunk = createAsyncThunk<
  AskAssistantResult,
  AssistantAskPayload,
  {
    rejectValue: AssistantAskRejectValue;
    state: RootState;
    dispatch: AppDispatch;
  }
>(
  'assistant/askAssistantThunk',
  async ({ question }, { getState, rejectWithValue, dispatch }) => {
    const { messages, isLoading } = getState().assistant;
    const lastMessage = messages[messages.length - 1];
    const sameQuestionInFlight =
      isLoading &&
      lastMessage?.role === 'user' &&
      lastMessage.text === question &&
      !!askAbortController &&
      !askAbortController.signal.aborted;

    if (sameQuestionInFlight) {
      return rejectWithValue({ aborted: true });
    }

    abortAssistantAsk();
    askAbortController = new AbortController();

    try {
      const data = await askAssistant(question, askAbortController.signal);
      askAbortController = null;
      dispatchNameHydration(dispatch, data.candidates, getState);

      let historyId: string | null = null;

      try {
        const saved = await saveHunterChatMessage({
          messageText: question,
          aiResponse: (data.candidates ?? []).map(candidate => ({
            ...candidate,
            relevance_score: 0,
          })),
        });
        historyId = saved.id;
      } catch (saveError) {
        console.warn('[assistant] failed to save chat history:', saveError);
      }

      return {
        ...data,
        historyId,
      };
    } catch (error) {
      const isAborted =
        askAbortController?.signal.aborted ||
        axios.isCancel(error) ||
        (axios.isAxiosError(error) && error.code === 'ERR_CANCELED');

      askAbortController = null;

      if (isAborted) {
        return rejectWithValue({ aborted: true });
      }

      if (error instanceof AssistantApiError) {
        return rejectWithValue({ messageKey: error.message });
      }

      return rejectWithValue({ messageKey: 'assistant.error' });
    }
  },
);

export const deleteAssistantHistoryThunk = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'assistant/deleteAssistantHistoryThunk',
  async (historyId, { rejectWithValue }) => {
    try {
      await deleteHunterChatHistory(historyId);
      return historyId;
    } catch (error) {
      console.warn('[assistant] failed to delete chat history:', error);
      return rejectWithValue('assistant.deleteError');
    }
  },
);

export const clearAssistantHistoryThunk = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>('assistant/clearAssistantHistoryThunk', async (_, { rejectWithValue }) => {
  try {
    await deleteAllHunterChatHistory();
    clearMiniChatScroll();
  } catch (error) {
    console.warn('[assistant] failed to clear chat history:', error);
    return rejectWithValue('assistant.clearAllError');
  }
});
